/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { TrackedArray } from 'tracked-built-ins';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';
import Papa from 'papaparse';
import { toCamelCase } from '@linc-technologies/linced-common/utils/strings';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import config from 'linced-app/config/environment';
import { compact, uniq } from '@linc-technologies/linced-common/utils/arrays';

const GROUP_IMPORT_COLUMNS = Object.freeze([
  'Teacher Email Address',
  'Class Name',
  'Class Team',
  'Calendar Year',
  'Student ID',
  'Student First Name',
  'Student Last Name',
]);
export default class GroupsController extends Controller {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  strings;

  Scopes = Scopes;
  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  importType = 'learners';

  fileTypes = Object.freeze(['text/csv', '.csv', '.hero']);

  @tracked
  file = null;

  @tracked
  fileChecked = false;

  @tracked
  fileIsValid = false;

  @tracked
  currentYear = moment().year();

  // Extras for Hero file processing.
  @computed('file.extension')
  get isHeroFile() {
    if (isEmpty(this.file) || isEmpty(this.file.extension)) {
      return false;
    }
    return this.file.extension === 'hero';
  }

  @tracked
  incorrectSchool = false;

  @tracked
  heroFileCheckInProgress = false;

  checkSchoolId(results) {
    let importSchoolId = results.data[0][0].trim();
    let thisSchoolId = this.get('session.school.jurisdictionalId').trim();
    this.heroFileCheckInProgress = false;
    this.incorrectSchool = importSchoolId !== thisSchoolId;
  }

  @tracked
  processingColumns = false;

  checkColumns(results) {
    if (this.isHeroFile) {
      // Drop the first row for Hero files, as it should only contain the school's jurisdictional ID,
      // and has already been checked by this point.
      results.data.shift();
    }
    let fileIsValid = false;
    let headers = results.data[0];
    if (headers.length === GROUP_IMPORT_COLUMNS.length) {
      // We have the right number of columns - check 'em.
      fileIsValid = headers.every((header, index) => {
        let expected = GROUP_IMPORT_COLUMNS[index];
        if (
          expected === 'Calendar Year' &&
          header === 'Calendar Year (optional)'
        ) {
          // Accept old variant of Calendar Year header.
          return true;
        }
        return header === expected;
      });
    }
    this.fileChecked = true;
    this.fileIsValid = fileIsValid;
    if (!fileIsValid) {
      this.file = null;
    }
  }

  @computed('model.homerooms.@each.year')
  get groupsByYear() {
    let years = compact(uniq(this.model.homerooms.map((item) => item.year)));
    return years.map((year) => {
      return {
        year,
        groups: this.model.homerooms.filter((item) => item.year === year),
      };
    });
  }

  @tracked
  groupsProcessing = false;

  @tracked
  learnersProcessing = false;

  // TrackedArray, not a plain array: processRow() pushes into this
  // incrementally as Papa.parse streams CSV rows in (processGroups()'s step
  // callback), and the template reads this.processedGroups directly via
  // {{#each}}/{{#if this.processedGroups.length}} for live progress.
  @tracked
  processedGroups = new TrackedArray();

  @tracked
  numRowsSeen = 0;

  @tracked
  numErrors = 0;

  @tracked
  numGroupsCreated = 0;

  @tracked
  numGroupsUpdated = 0;

  @tracked
  numGroupsUnchanged = 0;

  @tracked
  importing = false;

  @tracked
  seenHeader = false;

  @tracked
  seenHeroHeader = false;

  @tracked
  preview;

  @tracked
  columnMap;

  processRow(row) {
    // Skip Hero file header (if applicable).
    if (this.isHeroFile && !this.seenHeroHeader) {
      this.seenHeroHeader = true;
      return;
    }

    // Skip header row.
    if (!this.seenHeader) {
      this.seenHeader = true;
      return;
    }

    // Skip empty rows.
    let data = row.data;
    if (isEmpty(data) || data.every((cell) => isEmpty(cell))) {
      return;
    }

    let [teacherEmail, groupName, teamName, year, id, firstName, lastName] =
      data;

    if (isEmpty(year)) {
      // No year was provided, don't process this row.
      this.incrementProperty('numErrors');
      return;
    } else {
      year = parseInt(year, 10);
    }

    // Check if we've already matched this group.
    let group = this.processedGroups.find((group) => {
      return group.name === groupName && group.year === year;
    });

    if (isEmpty(group)) {
      let record = null;
      record = this.model.homerooms.find((group) => {
        return group.name === groupName && group.year === year;
      });
      if (isEmpty(record)) {
        record = this.model.homerooms.find((group) => {
          return group.label === groupName && group.year === year;
        });
      }

      this.processedGroups.push({
        name: groupName,
        team: teamName,
        year,
        group: record,
        // learnerStubs is built once here via native .push() (below) and
        // only ever read later via a read-only .forEach() in
        // processLearners() - never rendered directly, so a plain array is
        // fine. learners is different: it's cleared/rebuilt in
        // processLearners() and the template iterates it directly with
        // {{#each processed.learners}} - needs to stay reactive on mutation.
        learnerStubs: [],
        learners: new TrackedArray(),
        teacher: this.findTeacher(teacherEmail),
      });

      group = this.processedGroups.find((group) => {
        return group.name === groupName && group.year === year;
      });
    }

    if (isEmpty(group)) {
      // Something went wrong while mapping the group.  Count it as an error and move on.
      this.incrementProperty('numErrors');
      return;
    }

    group.learnerStubs.push({ id, firstName, lastName });
  }

  findTeacher(email) {
    return this.model.staff.find(
      (item) => item.fields['core:emailAddress']?.value === email,
    );
  }
  findLearner({ id, firstName, lastName }, group) {
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        // NSNs are automatically padded to 10 digits, so we'll need to do the same to make sure we get a match.
        if (!isEmpty(id)) {
          while (id.length < 10) {
            id = `0${id}`;
          }
        }
        break;
    }

    let person;
    if (!isEmpty(id)) {
      // We have an ID.  Search using that, as it should be the most reliable way to match.
      person = this.model.learners.find((item) => item.jurisdictionalId === id);
    } else {
      // We didn't get an ID.  This may be a pre-enrolled student; search by name to see if we can find them.
      // Legal names first
      person = this.model.learners.find(
        (item) => item.legalFullName === `${firstName} ${lastName}`,
      );
      if (isEmpty(person)) {
        // Now try preferred names
        person = this.model.learners.find(
          (item) => item.fullName === `${firstName} ${lastName}`,
        );
      }
    }

    if (isEmpty(person)) {
      // No existing learner found with the specified jurisdictional ID.
      return {
        id,
        firstName,
        lastName,
        status: 'Not found',
      };
    }

    let firstNameMatches =
      this.nameMatches(person.firstName, firstName) ||
      this.nameMatches(person.legalFirstName, firstName);
    let lastNameMatches =
      this.nameMatches(person.lastName, lastName) ||
      this.nameMatches(person.legalLastName, lastName);
    let nameMatches = firstNameMatches && lastNameMatches;
    let status = 'Already a member';
    if (
      isEmpty(group) ||
      !group.currentLearners.find((item) => item.personId === person.id)
    ) {
      status = 'Will be added';
    }

    return {
      id,
      firstName,
      lastName,
      person,
      status,
      nameDifferent: !nameMatches,
    };
  }

  nameMatches(a, b) {
    return a.toLowerCase().trim() === b.toLowerCase().trim();
  }

  parsingComplete() {
    this.groupsProcessing = false;
  }

  addLearnerToGroup(group, learner) {
    let member = group.learners.find((item) => item.personId === learner.id);
    if (isEmpty(member)) {
      group.learners.addFragment({
        personId: learner.id,
        status: 1, // STATUS_CURRENT = 1
      });
    } else {
      member.set('status', 1); // STATUS_CURRENT = 1
    }
  }

  removeLearnerFromGroup(group, learner) {
    let member = group.learners.find((item) => item.personId === learner.id);
    if (!isEmpty(member)) {
      member.set('status', 2); // STATUS_FORMER = 2
    }
  }

  @action
  setFile(file) {
    this.file = file;
    this.fileChecked = false;
    this.fileIsValid = false;
    Papa.parse(this.file.data, {
      header: false,
      preview: 2,
      complete: this.checkColumns.bind(this),
    });
  }

  @action
  removeFile() {
    this.file = null;
    this.fileChecked = false;
    this.fileIsValid = false;
  }

  @action
  checkHeroFile() {
    if (isEmpty(this.file) || !this.isHeroFile) {
      // No file provided yet, or not a Hero file.  No school check possible/required.
      return;
    }

    // Papa Parse is async, so we have to flag that we're checking so that we can prevent moving to the next step.
    this.heroFileCheckInProgress = true;
    this.incorrectSchool = false;

    Papa.parse(this.file.data, {
      header: false,
      preview: 1,
      complete: this.checkSchoolId.bind(this),
    });
  }

  @action
  processGroups() {
    this.groupsProcessing = true;
    this.processedGroups = new TrackedArray();
    this.seenHeader = false;
    this.numErrors = 0;
    this.currentYear = moment().year();
    // Although we have a well-defined set of columns, they're not really suitable for use as property names.
    // As such, we'll pretend there's no header row and skip it ourselves later.
    Papa.parse(this.file.data, {
      header: false,
      step: this.processRow.bind(this),
      complete: this.parsingComplete.bind(this),
    });
  }

  @action
  processLearners() {
    this.learnersProcessing = true;

    next(this, function () {
      this.processedGroups.forEach((processed) => {
        processed.learners.splice(0, processed.learners.length);
        processed.learnerStubs.forEach((learner) => {
          processed.learners.push(this.findLearner(learner, processed.group));
        });

        // If we have an existing group, remove any learners who have jurisdictional IDs but weren't in the import.
        if (!isEmpty(processed.group)) {
          processed.group.currentLearners.forEach((member) => {
            if (
              !processed.learners.find(
                (item) => item.person.id === member.personId,
              ) &&
              !isEmpty(member.get('person.jurisdictionalId'))
            ) {
              let learner = member.person;
              processed.learners.push({
                id: learner.jurisdictionalId,
                firstName: learner.firstName,
                lastName: learner.lastName,
                status: 'Will be removed',
                person: learner,
              });
            }
          });
        }
      });

      this.learnersProcessing = false;
    });
  }

  @action
  import() {
    this.importing = true;
    let promises = [];
    let teams = [];

    this.processedGroups.forEach((processed) => {
      let group = processed.group;
      let team = processed.team.trim();
      if (isEmpty(group)) {
        group = this.store.createRecord('group', {
          schoolId: this.session.school,
          name: processed.name,
          label: processed.name,
          year: processed.year,
          team,
          type: GroupType.HOMEROOM,
          isImported: true,
        });
        if (!isEmpty(processed.teacher)) {
          group.staff.addFragment({
            personId: processed.teacher.id,
            status: 1, // STATUS_CURRENT = 1
          });
        }
        teams.push(team);
      } else {
        if (!isEmpty(team) && toCamelCase(group.team) !== toCamelCase(team)) {
          group.set('team', team);
          teams.push(team);
        }
      }

      processed.learners.forEach((learner) => {
        if (!isEmpty(learner.person)) {
          switch (learner.status) {
            case 'Will be added':
              this.addLearnerToGroup(group, learner.person);
              break;
            case 'Will be removed':
              this.removeLearnerFromGroup(group, learner.person);
              break;
          }
        }
      });

      if (isEmpty(processed.group)) {
        promises.push(
          group
            .save()
            .then(() => {
              this.incrementProperty('numGroupsCreated');
            })
            .catch(() => {
              this.incrementProperty('numErrors');
            }),
        );
      } else if (group.hasDirtyAttributes) {
        promises.push(
          group
            .save()
            .then(() => {
              this.incrementProperty('numGroupsUpdated');
            })
            .catch(() => {
              this.incrementProperty('numErrors');
            }),
        );
      } else {
        this.incrementProperty('numGroupsUnchanged');
      }
    });

    // Update school's teams if necessary.
    promises.push(
      this.store
        .findRecord('school', this.get('session.activeSchool'))
        .then((school) => {
          let teamsOption = school.get('fields.core:groups:teams');
          if (isEmpty(teamsOption)) {
            school.addField('core:groups:teams', DataType.VALUE_LIST);
            teamsOption = school.get('fields.core:groups:teams');
          }

          let schoolTeams = teamsOption.get('value');
          let teamsUpdated = false;
          uniq(teams).forEach((team) => {
            let existing = schoolTeams.find((item) => item.value === team);
            if (isEmpty(existing)) {
              schoolTeams.pushObject({
                value: team,
                label: team,
              });
              teamsUpdated = true;
            }
          });

          if (teamsUpdated) {
            return school.save();
          }
          return true;
        }),
    );

    Promise.all(promises).then(() => {
      // After classes have been imported
      // calculate the auto groups
      this.calculateAutogroups();
      this.importing = false;
    });
  }

  calculateAutogroups() {
    let uri = `${config.APP.apiHost}/groups/${config.APP.apiPath}/groups/:recalculate`;
    let schoolId = this.get('session.activeSchool');
    let data = {
      schoolId: schoolId,
    };
    let options = {
      data: JSON.stringify(data),
    };

    this.fetch
      .post(uri, options)
      .then(() => {
        let query = {
          schoolId,
          year: moment().format('YYYY'),
        };
        this.store.query('group', query);
      })
      .catch(() => {
        this.paperToaster.show(`Error calculating auto group membership!`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  reset() {
    this.currentStep = 0;
    this.file = null;
    this.fileChecked = false;
    this.fileIsValid = false;
    this.processingColumns = false;
    this.preview = null;
    this.columnMap = [];
    this.numRowsSeen = 0;
    this.numErrors = 0;
    this.seenHeader = false;
    this.groupsProcessing = false;
    this.learnersProcessing = false;
    this.processedGroups = new TrackedArray();
    this.numGroupsCreated = 0;
    this.numGroupsUpdated = 0;
    this.numGroupsUnchanged = 0;
    this.seenHeroHeader = false;
    this.incorrectSchool = false;
    this.heroFileCheckInProgress = false;
  }
}
